import { isProduction } from '@/config';
import logger from '@/util/logger';
import { Loader2 } from 'lucide-react';
import { type FC, type PropsWithChildren, Suspense, useEffect } from 'react';

const log = logger('SuspenseTrap');

/**
 * Nothing in rock-admin is supposed to suspend — there is no `React.lazy`, and every async atom is
 * meant to be read through `@/state/loadableHooks`. A suspend inside one of the app's shared
 * boundaries unmounts the whole subtree; on the retry the atom refetches and suspends again, so a
 * single bare `useAtomValue(asyncAtom)` turns into a mount→suspend→remount loop that flashes the
 * spinner and hammers Firestore. Outside production we make that loud instead of plausible.
 */
export const SuspenseTrap: FC<PropsWithChildren<{ boundary: string }>> = ({ boundary, children }) => (
    <Suspense fallback={isProduction() ? <ContentLoading /> : <UnexpectedSuspense boundary={boundary} />}>
        {children}
    </Suspense>
);

export const ContentLoading: FC = () => (
    <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
);

/** The page's global `code` style is invisible against the red ground, so restate it locally. */
const Code: FC<PropsWithChildren> = ({ children }) => (
    <code className="rounded bg-black/25 px-1 py-0.5 font-mono text-[0.8125rem] text-inherit">{children}</code>
);

const UnexpectedSuspense: FC<{ boundary: string }> = ({ boundary }) => {
    useEffect(() => {
        log.error(`Unexpected Suspense in the '${boundary}' boundary`, {
            err: new Error('UnexpectedSuspense'),
            boundary,
        });
    }, [boundary]);

    return (
        <div className="flex h-full min-h-screen flex-col items-center justify-center gap-3 bg-destructive p-6 text-center text-destructive-foreground">
            <div className="text-2xl font-bold">Unexpected Suspense</div>
            <div className="text-sm opacity-90">Boundary: {boundary}</div>
            <div className="max-w-md text-sm leading-7">
                An async atom was read with <Code>useAtom()</Code> or <Code>useAtomValue()</Code>. Switch to{' '}
                <Code>useAsyncAtomValue()</Code> / <Code>useLoadableAtomValue()</Code> from{' '}
                <Code>@/state/loadableHooks</Code>. Check the console for the stack trace.
            </div>
        </div>
    );
};
